import { FC, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import Measure, { ContentRect } from 'react-measure';
import videojs from 'video.js';
import qualityLevels from 'videojs-contrib-quality-levels';

import { VideoImageSource } from '@xing-com/crate-common-graphql-types';

import { type PlayerApi as PlayerApiType } from '../../types/player-api';
import { EventHandler } from '../../utils/event-handler';
import { getPoster } from '../../utils/player';
import { PlayerApi } from '../../utils/player-api';
import {
  getDefaultVolume,
  getDefaultMutedState,
  getAllowAutoplayState,
} from '../../utils/settings';
import { VideojsGlobalStyles } from '../../utils/video-js.min';
import { VideojsIconsGlobalStyles } from '../../utils/videojs-icons';
import { addElementToVideojs } from '../controls/add-element';
import { AutoplaySettings } from '../controls/autoplay-settings/autoplay-settings';
import { Menu } from '../controls/menu/menu';
import { QualityLevels } from '../controls/quality-levels/quality-levels';
import { Subtitles, type Subtitle } from '../controls/subtitles/subtitles';
import { setupThumbnail } from '../controls/thumbnail/setup-thumbnail';

import * as Styled from './player.styles';

// _lazy_ load intersection observer polyfill
// but only if we are on browser, because
// the polyfill wants to populate the window object
if (typeof window !== 'undefined') {
  require('intersection-observer');
}

export type Props = {
  id: number | string;
  section: string;
  configuration: {
    autostart?: boolean;
    preload?: videojs.Preload | boolean;
    mute?: boolean;
    controls?: boolean;
    disablePictureInPicture?: boolean;
  };
  actions?: (player: PlayerApiType) => void;
  onReady?: () => void;
  onStart?: () => void;
  onMute?: (state: boolean) => void;
  onPlay?: () => void;
  onPause?: () => void;
  onComplete?: () => void;
  sources: videojs.Tech.SourceObject[] | string[];
  thumbnails?: PlayerThumbnail['sources'];
  posterImage?: string;
  sprites?: VideoImageSource[];
  subtitles?: Subtitle[];
  trackEventMutation?: (options: any) => void;
  forceAutoplay?: boolean;
  trackingExtraFields?: object;
  isStory?: boolean;
};
export const Player: FC<Props> = (props) => {
  const videoNode = useRef<HTMLVideoElement>(null);
  const xingPlayer = useRef<XingPlayer | null>(null);
  const playerApi = useRef<PlayerApiType | null>(null);
  const eventHandler = useRef<EventHandler | null>(null);
  const isInView = useRef(false);
  const [layout, setLayout] = useState('layout-normal');

  const playerReadyHandler = (): void => {
    if (!xingPlayer.current || !playerApi.current) {
      return;
    }

    // initialize video js api
    props.actions?.(playerApi.current);

    // init menu
    addElementToVideojs({
      parentNode: xingPlayer.current.videojs,
      player: xingPlayer.current.videojs,
      Component: Menu,
      props: {
        callback: (setMenu: () => void) => {
          if (xingPlayer.current) {
            xingPlayer.current.setMenu = setMenu;
          }
        },
      },
    });

    const controlBar = xingPlayer.current.videojs.getChild('controlBar');

    // add quality levels
    addElementToVideojs({
      parentNode: controlBar,
      player: xingPlayer.current.videojs,
      Component: QualityLevels,
      props: {
        xingPlayer: xingPlayer.current,
      },
      position: 8,
    });

    // add subtitles
    if (props.subtitles) {
      addElementToVideojs({
        parentNode: controlBar,
        player: xingPlayer.current.videojs,
        Component: Subtitles,
        props: {
          xingPlayer: xingPlayer.current,
          subtitles: props.subtitles,
          shouldDisplaySubtitle: xingPlayer.current.videojs.muted(),
        },
        position: 9,
      });
    }

    // add thumbnails
    if (props.sprites) {
      setupThumbnail(xingPlayer.current, props.sprites);
    }

    // initialize events/tracking
    intializeEventHandler();
  };

  const intializeEventHandler = (): void => {
    if (!xingPlayer.current) return;

    const trackEventMutation = props.trackEventMutation || (() => undefined);

    eventHandler.current = new EventHandler({
      xingPlayer: xingPlayer.current,
      section: props.section,
      extraFields: props.trackingExtraFields,
      eventsHandler: props,
      trackEventMutation: trackEventMutation,
    });
  };

  const [inViewRef] = useInView({
    threshold: [0, 0.5],
    onChange: (_inView, { intersectionRatio }) => {
      const inView = intersectionRatio > 0.5;

      if (isInView.current !== inView) {
        isInView.current = inView;

        if (eventHandler.current?.handleIntersection) {
          eventHandler.current.handleIntersection();
        }
      }
    },
  });

  const setBreakpoint = ({ bounds }: ContentRect): void => {
    const width = bounds?.width || 0;

    const newLayout =
      width < 350
        ? 'layout-tiny'
        : width < 500
          ? 'layout-small'
          : 'layout-normal';

    setLayout(newLayout);
  };

  useEffect(() => {
    if (!xingPlayer.current && videoNode.current) {
      const configuration = {
        autostart: false,
        preload: true,
        mute: getDefaultMutedState() ?? undefined,
        ...props.configuration,
      };

      const options: videojs.PlayerOptions = {
        aspectRatio: props.isStory ? '9:16' : '16:9',
        controls: configuration.controls || true,
        // resizeManager: false, TODO: this resizeManager is not available in videojs 7
        playsinline: true,
        sources: props.sources.map((source) => {
          if (typeof source === 'string') {
            /**
             * Allow webm video playback. This is needed for video playback on localhost.
             * In the past all sources were accepted. See:
             * https://source.xing.com/frontends/brewery/blob/master/packages/video/video/Player/Player.js#L64
             */
            const fileEnding = source.split('.').pop();
            return {
              src: source,
              type:
                fileEnding === 'webm' ? 'video/webm' : 'application/x-mpegURL',
            };
          }
          return source;
        }),
        poster:
          props.posterImage ??
          getPoster(
            props.thumbnails ?? [],
            videoNode.current?.parentElement?.clientWidth ?? 0
          ),
        autoplay:
          props.forceAutoplay ||
          (configuration.autostart && getAllowAutoplayState()),
        preload: configuration.preload as videojs.Preload,
        muted: props.forceAutoplay || configuration.mute,
        html5: {
          vhs: {
            overrideNative:
              window.navigator.userAgent.toLowerCase().indexOf('android') >= 0,
          },
          nativeAudioTracks: false,
          nativeVideoTracks: false,
        },
        disablePictureInPicture: configuration.disablePictureInPicture ?? false,
      };

      if (!videojs.getPlugin('qualityLevels')) {
        videojs.registerPlugin('qualityLevels', qualityLevels);
      }

      const player = videojs(videoNode.current, options, () => {
        playerReadyHandler();
      });

      player.on('ready', () => {
        const defaultVolume = getDefaultVolume();
        player.volume(defaultVolume);
      });

      xingPlayer.current = {
        videojs: player,
        videoId: props.id,
      };

      const playerApiMain = PlayerApi(player);
      playerApi.current = playerApiMain;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoNode]);

  useEffect(() => {
    return () => {
      const player = xingPlayer.current?.videojs;

      if (player && !player.isDisposed()) {
        player.dispose();
      }
    };
  }, []);

  // wrap the player in a div with a `data-vjs-player` attribute
  // so videojs won't create additional wrapper in the DOM
  // see https://github.com/videojs/video.js/pull/3856
  return (
    <Measure bounds onResize={(cr) => setBreakpoint(cr)}>
      {({ measureRef }) => (
        <Styled.VJSXing className={layout} $isStory={props.isStory}>
          <div data-vjs-player ref={measureRef}>
            <AutoplaySettings />
            <VideojsGlobalStyles />
            <VideojsIconsGlobalStyles />
            <div ref={inViewRef}>
              <video controls ref={videoNode} className="video-js">
                <track kind="captions" />
              </video>
            </div>
          </div>
        </Styled.VJSXing>
      )}
    </Measure>
  );
};
